import { API_BASE_URL } from "../config/api";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import PhoneActions from "../components/PhoneActions";

const formatAddress = (dealer) => {
  const address = dealer?.address || {};
  return [address.city, address.state].filter(Boolean).join(", ")
    || [address.street, address.area, address.city].filter(Boolean).join(", ")
    || "-";
};

export default function Dealers() {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const dealerBasePath = location.pathname.startsWith("/admin") ? "/admin/dealers" : "/accounts/dealers";

  useEffect(() => {
    const loadDealers = async () => {
      try {
        const res = await axios.get(API_BASE_URL + "/api/dealers");
        setDealers(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error("Failed to load dealers", error);
        setDealers([]);
      } finally {
        setLoading(false);
      }
    };

    loadDealers();
  }, []);

  const filteredDealers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return dealers.filter((dealer) =>
      [
        dealer.dealerId,
        dealer.supplierCode,
        dealer.name,
        dealer.companyName,
        dealer.phone,
        dealer.email,
        dealer.gstNumber,
        formatAddress(dealer)
      ]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [dealers, search]);

  return (
    <div className="space-y-4 lg:space-y-6">
      <section className="rounded-xl border border-amber-100 bg-white p-4 shadow-sm lg:rounded-[28px] lg:border-amber-200 lg:bg-[linear-gradient(135deg,_rgba(255,251,235,1),_rgba(255,255,255,1),_rgba(254,243,199,0.85))] lg:p-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="hidden text-sm font-semibold uppercase tracking-[0.22em] text-amber-700 lg:block">
              Supplier Register
            </p>
            <h1 className="text-2xl font-bold text-stone-950 lg:mt-2 lg:text-3xl">Supplier Directory</h1>
          </div>

          <div className="flex w-full gap-2 lg:w-auto lg:items-center">
            <input
              type="text"
              placeholder="Search supplier, code, company..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="min-w-0 flex-1 rounded-lg border border-amber-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-amber-400 lg:w-80 lg:rounded-2xl"
            />
            <button type="button" onClick={() => setSearch((current) => current.trim())} className="rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white lg:hidden">
              Search
            </button>

            <button
              onClick={() => navigate(`${dealerBasePath}/create`)}
              className="hidden items-center justify-center rounded-2xl bg-amber-500 px-5 py-3 text-sm font-semibold text-stone-950 shadow-sm transition hover:bg-amber-400 lg:inline-flex"
            >
              + Create Supplier
            </button>
          </div>
        </div>
        <button
          onClick={() => navigate(`${dealerBasePath}/create`)}
          className="mt-3 w-full rounded-lg bg-amber-500 px-4 py-3 text-sm font-semibold text-stone-950 shadow-sm transition hover:bg-amber-400 lg:hidden"
        >
          + Create Supplier
        </button>
      </section>

      {loading ? (
        <section className="rounded-[28px] border border-stone-200 bg-white px-5 py-12 text-center text-stone-500 shadow-sm">
          Loading suppliers...
        </section>
      ) : filteredDealers.length === 0 ? (
        <section className="rounded-[28px] border border-stone-200 bg-white px-5 py-12 text-center text-stone-500 shadow-sm">
          No suppliers found
        </section>
      ) : (
        <>
        <section className="space-y-3 lg:hidden">
          {filteredDealers.map((dealer) => (
            <SupplierMobileCard key={dealer._id} dealer={dealer} onOpen={() => navigate(`${dealerBasePath}/${dealer._id}`)} />
          ))}
        </section>

        <section className="hidden grid-cols-1 gap-5 lg:grid xl:grid-cols-2">
          {filteredDealers.map((dealer) => (
            <article
              key={dealer._id}
              className="rounded-[28px] border border-stone-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-400">
                    {dealer.supplierCode || dealer.dealerId || "Supplier"}
                  </p>
                  {dealer.supplierCode && dealer.dealerId ? (
                    <p className="mt-1 text-xs text-stone-400">{dealer.dealerId}</p>
                  ) : null}
                  <h2 className="mt-2 text-xl font-bold text-stone-900">{dealer.companyName || dealer.name || "-"}</h2>
                  <p className="mt-1 text-sm text-stone-500">{dealer.name || "-"}</p>
                  <p className="mt-2 text-sm text-stone-500">{formatAddress(dealer)}</p>
                </div>

                <button
                  type="button"
                  onClick={() => navigate(`${dealerBasePath}/${dealer._id}`)}
                  className="rounded-2xl bg-stone-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-black"
                >
                  View Details
                </button>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InfoCard label="Phone" value={<PhoneActions phone={dealer.phone || ""} />} />
                <InfoCard label="Email" value={dealer.email || "-"} />
                <InfoCard label="GST Number" value={dealer.gstNumber || "-"} />
                <InfoCard label="Contact" value={dealer.name || "-"} />
                <InfoCard label="Supplier Code" value={dealer.supplierCode || "-"} />
              </div>
            </article>
          ))}
        </section>
        </>
      )}
    </div>
  );
}

function SupplierMobileCard({ dealer, onOpen }) {
  return (
    <article className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-stone-100" onClick={onOpen}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-400">
        {dealer.supplierCode || dealer.dealerId || "Supplier"}
      </p>
      <h2 className="mt-2 text-lg font-bold leading-snug text-stone-950">{dealer.companyName || dealer.name || "-"}</h2>
      <div className="mt-3 rounded-xl border border-stone-100 bg-stone-50 px-3 py-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-500">Contact Person</p>
        <p className="mt-1 text-sm text-stone-700">{dealer.name || "-"}</p>
        <div className="mt-2 text-sm text-stone-700">
          <PhoneActions phone={dealer.phone || ""} />
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 text-sm">
        <span className="font-semibold uppercase tracking-[0.18em] text-stone-500">Additional Info</span>
        <span className="truncate text-stone-700">{dealer.gstNumber || formatAddress(dealer)}</span>
      </div>
    </article>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="rounded-3xl border border-stone-200 bg-stone-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-400">{label}</p>
      <div className="mt-2 text-sm leading-6 text-stone-700">{value}</div>
    </div>
  );
}


